import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

const BoiteAmineProject: React.FC = () => {
  return (
    <Box>
      {/* Header avec titre et image */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <img
          src="/Twitch/BoiteAmine.png"
          alt="Logo La grosse boîte d'Amine"
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
          }}
        />
        <Typography
          variant="h4"
          component="h2"
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          La grosse boîte d'Amine
        </Typography>
      </Box>

      {/* Statut et date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            2024
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        Projet interactif créé pour le streamer AmineMaTue : une imposante boîte en bois 
        (style container maritime) contenant un objet mystère. Les viewers utilisent 
        leurs points de chaîne pour proposer des réponses et tenter de deviner le contenu. 
        La boîte intègre un système électronique qui l'ouvre automatiquement 
        lorsque la bonne réponse est trouvée et validée par le streamer.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Fonctionnalités principales */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Fonctionnalités principales
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 2 
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Système de paris avec points de chaîne
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Interface streamer temps réel
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Validation/refus des réponses en live
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Serveur persistant 24h/24
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Ouverture automatique électronique
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Chat en écoute permanente
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Intégration Arduino + Raspberry Pi
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Technologies */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Technologies utilisées
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['Express.js', 'React', 'Twitch API', 'Arduino', 'Raspberry Pi', 'Node.js', 'Électronique embarquée', 'WebSocket'].map((tech) => (
            <Chip
              key={tech}
              label={tech}
              variant="filled"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Défis techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Défis techniques relevés
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Intégration hardware/software :</strong> Synchronisation parfaite entre l'application 
          web, l'API Twitch et les systèmes électroniques Arduino/Raspberry Pi pour déclencher 
          l'ouverture automatique de la boîte.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Fiabilité 24h/24 :</strong> Mise en place d'un serveur robuste capable d'écouter 
          le chat Twitch en permanence, même hors stream, avec gestion des reconnexions automatiques 
          et système de fail-safe.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Conception mécanique :</strong> Développement d'un système de verrouillage sécurisé 
          avec vérins pneumatiques et verrous magnétiques.
        </Typography>
        <Typography variant="body2">
          <strong>UX temps réel :</strong> Interface streamer intuitive permettant la validation 
          instantanée des réponses avec feedback visuel immédiat et historique des tentatives 
          des viewers.
        </Typography>
      </Box>
    </Box>
  );
};

export default BoiteAmineProject;

